'use client';
import { QUERY_KEYS } from '@/constants';
import { useOpenAskModal } from '@/contexts/askModal';
import { useAuthContext } from '@/contexts/auth';
import { usePopupConfirmContext } from '@/contexts/popup';
import {
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { conversationService } from '../services/conversationService';
import type { IChat, IFeedback, IReaction } from '../types';
import type { IPagination } from '../types/common';

export const useConversations = (params: IPagination) => {
	return useInfiniteQuery({
		queryKey: [QUERY_KEYS.CONVERSATIONS, params],
		queryFn: ({ pageParam = 1 }) =>
			conversationService.searchConversations({
				...params,
				page: pageParam,
				size: params.size,
			}),
		getNextPageParam: (lastPage) =>
			lastPage.page.last ? undefined : lastPage.page.number + 1,
		initialPageParam: 1,
		enabled: !!params.size,
	});
};

export const useConversationChat = () => {
	const queryClient = useQueryClient();
	const { isGuest } = useAuthContext();
	const router = useRouter();
	const { show } = usePopupConfirmContext();
	const { onClose } = useOpenAskModal();
	const { t } = useTranslation('common');

	return useMutation({
		mutationFn: (data: IChat) => {
			if (isGuest) {
				show({
					message: t('ForbiddenException: Please sign in to chat'),
					onConfirm: () => {
						onClose();
						router.push('/my_profile/login');
					},
				});
				return Promise.reject(new Error('Guest user cannot chat'));
			}
			return conversationService.chat(data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CONVERSATIONS] });
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.CONVERSATION_MESSAGES],
			});
		},
	});
};

export const useTrendingConversations = ({ page, size }: IPagination) => {
	return useInfiniteQuery({
		queryKey: [QUERY_KEYS.TRENDING_CONVERSATIONS, page, size],
		queryFn: ({ pageParam = 0 }) =>
			conversationService.getTrending({ page: pageParam, size }),
		getNextPageParam: (lastPage) =>
			lastPage.page.last ? undefined : lastPage.page.number + 1,
		initialPageParam: 0,
		enabled: !!size,
	});
};

export const useRisingConversations = ({ page, size }: IPagination) => {
	return useInfiniteQuery({
		queryKey: [QUERY_KEYS.RISING_CONVERSATIONS, page, size],
		queryFn: ({ pageParam = 0 }) =>
			conversationService.getRising({ page: pageParam, size }),
		getNextPageParam: (lastPage) =>
			lastPage.page.last ? undefined : lastPage.page.number + 1,
		initialPageParam: 0,
		enabled: !!size,
	});
};

export const useNewConversations = ({ page, size }: IPagination) => {
	return useInfiniteQuery({
		queryKey: [QUERY_KEYS.NEW_CONVERSATIONS, page, size],
		queryFn: ({ pageParam = 0 }) =>
			conversationService.getNew({ page: pageParam, size }),
		getNextPageParam: (lastPage) =>
			lastPage.page.last ? undefined : lastPage.page.number + 1,
		initialPageParam: 0,
		enabled: !!size,
	});
};

export const useMostViewedConversations = ({ page, size }: IPagination) => {
	return useInfiniteQuery({
		queryKey: [QUERY_KEYS.MOST_VIEWED_CONVERSATIONS, page, size],
		queryFn: ({ pageParam = 0 }) =>
			conversationService.getMostViewed({ page: pageParam, size }),
		getNextPageParam: (lastPage) =>
			lastPage.page.last ? undefined : lastPage.page.number + 1,
		initialPageParam: 0,
		enabled: !!size,
	});
};

export const useForYouTrendingConversations = ({ page, size }: IPagination) => {
	return useInfiniteQuery({
		queryKey: [QUERY_KEYS.FOR_YOU_TRENDING, page, size],
		queryFn: ({ pageParam = 0 }) =>
			conversationService.getForYouTrending({ page: pageParam, size }),
		getNextPageParam: (lastPage) =>
			lastPage.page.last ? undefined : lastPage.page.number + 1,
		initialPageParam: 0,
		enabled: !!size,
	});
};

export const useForYouFollowUpConversations = ({ page, size }: IPagination) => {
	return useInfiniteQuery({
		queryKey: [QUERY_KEYS.FOR_YOU_FOLLOW_UP, page, size],
		queryFn: ({ pageParam = 0 }) =>
			conversationService.getForYouFollowUp({ page: pageParam, size }),
		getNextPageParam: (lastPage) =>
			lastPage.page.last ? undefined : lastPage.page.number + 1,
		initialPageParam: 0,
		enabled: !!size,
	});
};

export const useConversationReactions = (conversationId: string) => {
	const queryClient = useQueryClient();
	const { isGuest } = useAuthContext();
	const router = useRouter();
	const { show } = usePopupConfirmContext();
	const { t } = useTranslation('common');

	const reactionMutation = useMutation({
		mutationFn: (data: IReaction) => {
			if (isGuest) {
				show({
					message: t('ForbiddenException: Please sign in to react'),
					onConfirm: () => {
						router.push('/my_profile/login');
					},
				});
				return Promise.reject(new Error('Guest user cannot react'));
			}
			return conversationService.react(conversationId, data);
		},
		onSuccess: () => {
			for (const key of [
				QUERY_KEYS.FOR_YOU_FOLLOW_UP,
				QUERY_KEYS.FOR_YOU_TRENDING,
				QUERY_KEYS.CONVERSATIONS,
				QUERY_KEYS.CONVERSATION_DETAILS,
			]) {
				queryClient.invalidateQueries({ queryKey: [key] });
			}
		},
	});

	return {
		likeConversationFn: reactionMutation.mutate,
		isReacting: reactionMutation.isPending,
		reactionError: reactionMutation.error,
	};
};

export const useUpdateConversationSharingStatus = (conversationId: string) => {
	const queryClient = useQueryClient();
	const { isGuest } = useAuthContext();
	const router = useRouter();
	const { show } = usePopupConfirmContext();
	const { t } = useTranslation('common');

	const updateSharingStatusMutation = useMutation({
		mutationFn: (data: { sharingStatus: 'PRIVATE' | 'PUBLIC' }) => {
			if (isGuest) {
				show({
					message: t(
						'ForbiddenException: Please sign in to update sharing status',
					),
					onConfirm: () => {
						router.push('/my_profile/login');
					},
				});
				return Promise.reject(
					new Error('Guest user cannot update sharing status'),
				);
			}
			return conversationService.updateSharingStatus(
				conversationId,
				data.sharingStatus,
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.CONVERSATION_DETAILS, conversationId],
			});
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.CONVERSATIONS],
			});
		},
	});

	return {
		updateSharingStatusFn: updateSharingStatusMutation.mutate,
		isUpdatingSharingStatus: updateSharingStatusMutation.isPending,
		updateSharingStatusError: updateSharingStatusMutation.error,
	};
};

export const useGetConversationComments = (
	conversationId: string,
	{ page, size }: IPagination,
) => {
	return useInfiniteQuery({
		queryKey: [QUERY_KEYS.CONVERSATION_COMMENTS, conversationId, page, size],
		queryFn: ({ pageParam = 0 }) =>
			conversationService.getComments(conversationId, {
				page: pageParam,
				size,
			}),
		getNextPageParam: (lastPage) =>
			lastPage.page.last ? undefined : lastPage.page.number + 1,
		initialPageParam: 0,
		enabled: !!size,
	});
};

export const useGetCommentReplies = (
	commentId: number,
	{ page, size }: IPagination,
) => {
	return useInfiniteQuery({
		queryKey: ['comment-replies', commentId, page, size],
		queryFn: ({ pageParam = 0 }) =>
			conversationService.getCommentReplies(commentId, {
				page: pageParam,
				size,
			}),
		getNextPageParam: (lastPage) =>
			lastPage.page.last ? undefined : lastPage.page.number + 1,
		initialPageParam: 0,
		enabled: !!size,
	});
};

export const useConversationDetails = (
	conversationId: string,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: [QUERY_KEYS.CONVERSATION_DETAILS, conversationId],
		queryFn: () => conversationService.getDetails(conversationId),
		...options,
	});
};

export const useConversationMessages = (
	conversationId: string,
	{ page, size }: IPagination,
) => {
	return useInfiniteQuery({
		queryKey: [QUERY_KEYS.CONVERSATION_MESSAGES, conversationId, page, size],
		queryFn: ({ pageParam = 0 }) =>
			conversationService.getMessages(conversationId, {
				page: pageParam,
				size,
			}),
		getNextPageParam: (lastPage) =>
			lastPage.page.last ? undefined : lastPage.page.number + 1,
		initialPageParam: 0,
		enabled: !!size,
	});
};

export const useFeedbackMessage = (chatMessageId: number) => {
	return useMutation({
		mutationFn: (data: IFeedback) =>
			conversationService.provideFeedback(chatMessageId, data),
	});
};

export const useConversationFollowUp = (chatMessageId: number) => {
	const followUpQuery = useQuery({
		queryKey: [QUERY_KEYS.CONVERSATION_FOLLOW_UP, chatMessageId],
		queryFn: () => conversationService.followUp({ chatMessageId }),
		enabled: !!chatMessageId,
	});

	return {
		data: followUpQuery.data,
		isLoading: followUpQuery.isLoading,
		error: followUpQuery.error,
	};
};

export const useMessageTags = (chatMessageId: number) => {
	return useQuery({
		queryKey: [QUERY_KEYS.CONVERSATION_MESSAGE_TAGS, chatMessageId],
		queryFn: () => conversationService.getMessageTags(chatMessageId),
		enabled: !!chatMessageId,
	});
};

export const useMessageContent = (chatMessageId: number) => {
	return useQuery({
		queryKey: [QUERY_KEYS.CONVERSATION_MESSAGE_CONTENT, chatMessageId],
		queryFn: () => conversationService.getMessageContent(chatMessageId),
		enabled: !!chatMessageId,
	});
};
